import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { applyParamPatch, mapSummaryFilterKeys, parseAdminParams } from "./adminUrlParams";

/* Fallback source for a bare /gh-admin hit (an old bookmark, or a history
   entry from before this existed) — the three keys the tab/subtab/calendar
   used to persist to exclusively. Kept in sync below so a bare hit always
   has something recent to fall back to. */
const LS_TAB = "admin_active_tab";
const LS_GH_SUB = "admin_gh_subtab";
const LS_CALENDAR = "admin_show_calendar";

/* Every tab/filter/search/open-booking the admin dashboard needs to survive
   a page refresh lives in the URL's query string rather than component
   state (TODO #9) — it survives refresh for free and comes with
   shareable/bookmarkable links. This hook is the only place in the codebase
   that touches routing for that: every other admin component stays
   router-agnostic and receives its slice of this as plain props/callbacks
   from AdminDashboard. */
export default function useAdminUrlState() {
  const [searchParams, setSearchParamsRaw] = useSearchParams();

  const patch = useCallback(
    (updates, { replace = true } = {}) => {
      setSearchParamsRaw((prev) => applyParamPatch(prev, updates), { replace });
    },
    [setSearchParamsRaw],
  );

  useEffect(() => {
    if ([...searchParams.keys()].length > 0) return;
    const savedTab = localStorage.getItem(LS_TAB);
    const tab = savedTab && savedTab !== "room-overview" ? savedTab : "summary";
    const updates = { tab };
    const savedGhSub = localStorage.getItem(LS_GH_SUB);
    if (savedGhSub) updates.gh_sub = savedGhSub;
    if (localStorage.getItem(LS_CALENDAR) === "true") updates.calendar = "1";
    patch(updates, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = useMemo(() => parseAdminParams(searchParams), [searchParams]);

  useEffect(() => {
    localStorage.setItem(LS_TAB, params.tab);
  }, [params.tab]);
  useEffect(() => {
    if (params.ghSub) localStorage.setItem(LS_GH_SUB, params.ghSub);
  }, [params.ghSub]);
  useEffect(() => {
    localStorage.setItem(LS_CALENDAR, String(params.calendar));
  }, [params.calendar]);

  /* A real "going somewhere new" navigation: pushes, so Back steps out of it. */
  const setTab = useCallback(
    (tab) => patch({ tab, b: null, view: null, calendar: null, room: null }, { replace: false }),
    [patch],
  );

  const openDetail = useCallback(
    (id, { view = false } = {}) =>
      patch({ b: id, view: view ? "1" : null, calendar: null }, { replace: false }),
    [patch],
  );

  const openDetailFromRoomMap = useCallback(
    (id, { roomId, gh, date } = {}) =>
      patch({ b: id, room: roomId, gh, date, view: null, calendar: null }, { replace: false }),
    [patch],
  );

  /* Removes only the detail overlay — `room` is left for RoomMapTab's own
     initialSelection effect to consume and clear once it has re-opened the
     popup it was opened from. */
  const closeDetail = useCallback(() => patch({ b: null, view: null }, { replace: true }), [patch]);

  const clearRoom = useCallback(() => patch({ room: null }, { replace: true }), [patch]);

  const toggleCalendar = useCallback(
    () => patch({ calendar: params.calendar ? null : "1", b: null, room: null }, { replace: true }),
    [patch, params.calendar],
  );

  const setGhSub = useCallback((v) => patch({ gh_sub: v }, { replace: true }), [patch]);

  const openGuestHouseManage = useCallback(
    (ghSub) =>
      patch({ tab: "guest-house", gh_sub: ghSub, b: null, room: null, calendar: null }, { replace: false }),
    [patch],
  );

  const openRoomOverview = useCallback(
    (ghId) => patch({ tab: "room-overview", gh: ghId, b: null, room: null, calendar: null }, { replace: false }),
    [patch],
  );

  const openBookingsFiltered = useCallback(
    (status, gh) =>
      patch(
        { tab: "bookings", status, gh, view: "1", b: null, room: null, calendar: null },
        { replace: false },
      ),
    [patch],
  );

  const setStat = useCallback((v) => patch({ stat: v }, { replace: true }), [patch]);

  const setBookingsFilter = useCallback(
    (p) => {
      const mapped = {};
      if ("status" in p) mapped.status = p.status;
      if ("gh" in p) mapped.gh = p.gh;
      if ("q" in p) mapped.q = p.q;
      if ("from" in p) mapped.from = p.from;
      if ("to" in p) mapped.to = p.to;
      patch(mapped, { replace: true });
    },
    [patch],
  );

  const setSummaryFilter = useCallback((p) => patch(mapSummaryFilterKeys(p), { replace: true }), [patch]);

  const setFrontDeskFilter = useCallback(
    (p) => {
      const mapped = {};
      if ("gh" in p) mapped.gh = p.gh;
      if ("fdQuery" in p) mapped.fd_q = p.fdQuery;
      patch(mapped, { replace: true });
    },
    [patch],
  );

  const setRoomMapView = useCallback(
    (p) => {
      const mapped = {};
      if ("gh" in p) mapped.gh = p.gh;
      if ("date" in p) mapped.date = p.date;
      patch(mapped, { replace: true });
    },
    [patch],
  );

  /* Course-correction for a param that turned out not to resolve (a stale
     bookmark's room-overview guest house, a 404'd booking id) — replace, not
     push, since it isn't a navigation the admin asked for. */
  const forceTab = useCallback((tab) => patch({ tab }, { replace: true }), [patch]);

  const goHome = useCallback(
    () =>
      patch(
        { tab: "summary", b: null, view: null, calendar: null, room: null, stat: "All" },
        { replace: false },
      ),
    [patch],
  );

  return {
    params,
    setTab,
    openDetail,
    openDetailFromRoomMap,
    closeDetail,
    clearRoom,
    toggleCalendar,
    setGhSub,
    openGuestHouseManage,
    openRoomOverview,
    openBookingsFiltered,
    setStat,
    setBookingsFilter,
    setSummaryFilter,
    setFrontDeskFilter,
    setRoomMapView,
    forceTab,
    goHome,
  };
}
